// Multi-zone Quick Booking helpers (RON-5769, API contract RON-5943).
//
// Step 1 of BookParkingOrDesk / BookMeeting lets the user pick several zones.
// Every step-1 control (Type, Vehicle, Tags, time points) is built from the
// QuickBookingInfo_api ZoneList entries of ALL selected zones, merged here, and
// SpotAvailability_api is then called once with `ZoneId: [ids]`. Its response
// moves the per-zone metadata (cost, pre-booking question, zone date/time,
// status) into `Records.Zones[]`, matched to a space by `ZoneId`.

// SpotAvailability_api rejects more than 20 zone ids.
export const MAX_SELECTED_ZONES = 20;

const asArray = (value) => (Array.isArray(value) ? value : []);

// ZoneList entries for the selected ids, in ZoneList order.
export const getSelectedZones = (zoneList = [], selectedZoneIds = []) => {
  const ids = new Set(asArray(selectedZoneIds).map(String));
  return asArray(zoneList).filter((zone) => ids.has(String(zone?.Id)));
};

// The selection the dropdown may move to. At least one zone always stays
// selected (the API rejects an empty list and step 1 would have nothing to show),
// and the API cap is enforced by refusing the extra pick.
export const applyZoneSelection = (currentIds = [], nextIds = []) => {
  const next = asArray(nextIds);
  if (next.length === 0 || next.length > MAX_SELECTED_ZONES) return asArray(currentIds);
  return next;
};

// Type: All day / Hourly once each, when ANY selected zone supports it.
export const getZoneTypeFlags = (zones = []) => ({
  hasDaily: asArray(zones).some((zone) => Number(zone?.IsDailySpotExist) === 1),
  hasHourly: asArray(zones).some((zone) => Number(zone?.IsHourlySpotExist) === 1),
});

// Vehicles: one entry per VehiclePrimaryKey across the selected zones.
// A vehicle enabled in at least one selected zone is enabled - the search still
// returns spaces from the zones it fits. It is disabled only when every selected
// zone that lists it disables it. Enabled vehicles come first, so index 0 is an
// enabled vehicle whenever there is one.
export const mergeZoneVehicles = (zones = []) => {
  const enabled = new Map();
  const disabled = new Map();

  asArray(zones).forEach((zone) => {
    asArray(zone?.EmployeeEnabledVehicle).forEach((vehicle) => {
      if (!enabled.has(vehicle.VehiclePrimaryKey)) enabled.set(vehicle.VehiclePrimaryKey, vehicle);
    });
    asArray(zone?.EmployeeDisabledVehicle).forEach((vehicle) => {
      if (!disabled.has(vehicle.VehiclePrimaryKey)) disabled.set(vehicle.VehiclePrimaryKey, vehicle);
    });
  });

  enabled.forEach((_, key) => disabled.delete(key));

  return [
    ...[...enabled.values()].map((vehicle) => ({ ...vehicle, isDisabledVehicle: false })),
    ...[...disabled.values()].map((vehicle) => ({ ...vehicle, isDisabledVehicle: true })),
  ];
};

// Tags: one entry per tag Id, first-seen order.
export const mergeZoneTags = (zones = []) => {
  const tags = new Map();
  asArray(zones).forEach((zone) => {
    asArray(zone?.Tags).forEach((tag) => {
      if (!tags.has(tag.Id)) tags.set(tag.Id, tag);
    });
  });
  return [...tags.values()];
};

// Time points: union of every selected zone's TimeInterval, sorted.
// "HH:mm" strings sort chronologically as plain text.
export const mergeZoneTimePoints = (zones = []) =>
  [...new Set(asArray(zones).flatMap((zone) => asArray(zone?.TimeInterval)))].sort();

// The Records.Zones[] entry of one zone.
export const findZoneMeta = (zonesMeta = [], zoneId) =>
  asArray(zonesMeta).find((zone) => String(zone?.ZoneId) === String(zoneId)) || null;

// A top-level 200 can still carry zones that failed (412 past date, 486 existing
// booking). The first failing zone, wrapped as an API response so it goes
// through handleApiUiResponse like any other error (orange popup or red toaster).
export const getFirstZoneErrorResponse = (zonesMeta = []) => {
  const failedZone = asArray(zonesMeta).find(
    (zone) => zone?.ResponseCode !== undefined && Number(zone.ResponseCode) !== 200
  );
  if (!failedZone) return null;

  return {
    Data: {
      ResponseCode: Number(failedZone.ResponseCode),
      ErrorMessage: failedZone.ErrorMessage || "",
      Message: failedZone.Message || "",
    },
  };
};
